/**
 * F21 (Wave 2D) — admin payment-config detail page (read-only).
 *
 * Route: `/venues/:venueId/payment-config`
 * Permission: `payments:read` (super_admin + support + finance).
 *
 * Read-only view of the venue's current payment configuration + the latest
 * 20 `admin_audit_log` rows scoped to `payment_config.*` actions. The
 * `payment_config:write` permission gates the Apply / Clear override actions
 * which live on the sibling `override.tsx` page (linked from the CTA on this
 * page; finance role sees a "read-only" banner instead).
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  AuditLogTimeline,
  Banner,
  Button,
  Card,
  Chip,
  EmptyState,
  Skeleton,
} from '@bravemove/ui-web';
import type { AuditEvent } from '@bravemove/ui-web';
import { useAuth } from '../../../../auth/useAuth';
import {
  adminPaymentConfigApi,
  type AdminPaymentConfigAuditEntry,
  type AdminPaymentConfigResponse,
} from '../../../../api/paymentConfig';
import { translateApiError } from '../../../../auth/errors';

function actionToKind(action: string): AuditEvent['kind'] {
  if (action.endsWith('.admin_override_apply')) return 'permission';
  if (action.endsWith('.admin_override_clear')) return 'permission';
  if (action.endsWith('.update')) return 'update';
  return 'info';
}

function auditEntryToEvent(entry: AdminPaymentConfigAuditEntry): AuditEvent {
  return {
    id: entry.id,
    at: entry.createdAt,
    kind: actionToKind(entry.action),
    summary: entry.action,
    // BP-06 — the route returns `actorEmail` (admin_audit_log.actor_email);
    // 'System' is only the fallback for rows with no authenticated actor.
    actor: { name: entry.actorEmail ?? 'System' },
  };
}

export function AdminVenuePaymentConfigPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const auth = useAuth();
  const { venueId } = useParams<{ readonly venueId: string }>();
  const canWrite = auth.adminUser?.permissions?.includes('payment_config:write') ?? false;

  const [data, setData] = useState<AdminPaymentConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!venueId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await adminPaymentConfigApi.detail(venueId);
      setData(res);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [venueId, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- standard load-on-mount
    void fetchDetail();
  }, [fetchDetail]);

  if (!venueId) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-4" data-testid="admin-payment-config-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div data-testid="admin-payment-config-error">
        <Banner tone="danger" title={error ?? 'Failed to load payment configuration'} />
      </div>
    );
  }

  const { config, auditHistory } = data;

  return (
    <div className="flex flex-col gap-6" data-testid="admin-payment-config-page">
      <header className="flex flex-col gap-2">
        <h1 className="text-h1 font-bold text-ink">{t('payment-config:tab.title')}</h1>
        <p className="text-body text-ink-2">{t('payment-config:tab.description')}</p>
      </header>

      {!canWrite ? (
        <div data-testid="admin-payment-config-readonly-banner">
          <Banner
            tone="info"
            title={t('payment-config:validation.PAYMENT_CONFIG_FORBIDDEN', {
              defaultValue: 'Read-only view',
            })}
          >
            {t('payment-config:tab.description')}
          </Banner>
        </div>
      ) : null}

      <Card>
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-h2 font-bold text-ink">
              {t('payment-config:methods.section_title')}
            </h2>
            {config.adminOverride !== null ? (
              <Chip data-testid="admin-payment-config-override-active">
                {t('payment-config:admin_override.banner.title')}
              </Chip>
            ) : null}
          </div>

          {config.adminOverride !== null ? (
            <div data-testid="admin-payment-config-override-meta">
              <Banner tone="warning" title={t('payment-config:admin_override.banner.title')}>
                <div className="flex flex-col gap-1 text-[13px] text-ink">
                  <div>
                    <span className="font-mono text-[11px] uppercase tracking-wider text-ink-3 me-2">
                      {t('payment-config:admin_override.banner.set_by_label')}
                    </span>
                    {config.adminOverride.setByAdminDisplayName}
                  </div>
                  <div>
                    <span className="font-mono text-[11px] uppercase tracking-wider text-ink-3 me-2">
                      {t('payment-config:admin_override.banner.set_at_label')}
                    </span>
                    {config.adminOverride.setAt}
                  </div>
                  <div>
                    <span className="font-mono text-[11px] uppercase tracking-wider text-ink-3 me-2">
                      {t('payment-config:admin_override.banner.reason_label')}
                    </span>
                    {config.adminOverride.reason}
                  </div>
                </div>
              </Banner>
            </div>
          ) : null}

          <section>
            <h3 className="text-h3 font-bold text-ink mb-2">
              {t('payment-config:methods.section_title')}
            </h3>
            <div className="flex flex-wrap gap-2" data-testid="admin-payment-config-methods">
              {config.acceptedMethods.length === 0 ? (
                <span className="text-[12.5px] text-ink-3">—</span>
              ) : (
                config.acceptedMethods.map((m) => (
                  <Chip key={m} data-testid={`admin-payment-config-method-${m}`}>
                    {t(`payment-config:methods.${m}.label`)}
                  </Chip>
                ))
              )}
            </div>
            {config.payoutInstructions.length > 0 ? (
              <ul className="mt-3 space-y-2">
                {config.payoutInstructions.map((pi, i) => (
                  <li
                    key={`${pi.method}-${i}`}
                    className="rounded-md border border-hairline-2 bg-surface p-3 text-[13px] text-ink"
                    data-testid={`admin-payment-config-instruction-${pi.method}`}
                  >
                    <div className="font-semibold mb-0.5">
                      {pi.label || t(`payment-config:methods.${pi.method}.label`)}
                    </div>
                    {pi.method === 'whish' ? (
                      <div className="font-mono text-[12px] text-ink-2">{pi.whishPhone}</div>
                    ) : null}
                    {pi.method === 'omt' ? (
                      <div className="text-[12.5px] text-ink-2">
                        {pi.omtBeneficiary}
                        {pi.omtBranch ? ` · ${pi.omtBranch}` : ''}
                      </div>
                    ) : null}
                    {pi.details ? (
                      <p className="text-[12px] text-ink-2 mt-1 whitespace-pre-wrap">
                        {pi.details}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
          </section>

          <section>
            <h3 className="text-h3 font-bold text-ink mb-2">
              {t('payment-config:billing.section_title')}
            </h3>
            <div data-testid="admin-payment-config-billing-model">
              <span className="text-[13.5px] font-semibold text-ink">
                {t(`payment-config:billing.${config.billingModel}.title`)}
              </span>
              <p className="text-[12.5px] text-ink-2 mt-0.5">
                {t(`payment-config:billing.${config.billingModel}.description`)}
              </p>
            </div>
          </section>

          {canWrite ? (
            <div className="flex flex-wrap gap-2 border-t border-hairline pt-3">
              <Button
                variant="primary"
                size="sm"
                onClick={() =>
                  navigate(`/venues/${encodeURIComponent(venueId)}/payment-config/override`)
                }
                data-testid="admin-payment-config-override-cta"
              >
                {config.adminOverride !== null
                  ? t('payment-config:admin_override.clear_form.title')
                  : t('payment-config:admin_override.apply_form.title')}
              </Button>
            </div>
          ) : null}
        </div>
      </Card>

      <Card>
        <div className="flex flex-col gap-3">
          {/* BP-11 — dedicated section-title/empty keys; the previous
              `audit.update_by_owner` reuse rendered its action label
              ("Payment configuration updated") as the heading. */}
          <h2 className="text-h2 font-bold text-ink">
            {t('payment-config:audit.section_title', { defaultValue: 'Audit history' })}
          </h2>
          {auditHistory.length === 0 ? (
            <EmptyState
              title={t('payment-config:audit.empty_title', {
                defaultValue: 'No audit entries yet',
              })}
            />
          ) : (
            <AuditLogTimeline events={auditHistory.map(auditEntryToEvent)} />
          )}
        </div>
      </Card>
    </div>
  );
}

export default AdminVenuePaymentConfigPage;
